<template>
  <!-- 핸드폰 扫码 — 上架(위치번호 → 상품/箱号 연속) · 托盘箱号确认(托盘号 → 箱号 연속).
       PC(LocationLabelScan·PalletBoxScan)와 같은 서버 API(putawayScan.js). 카메라 + 수동 입력칸(블루투스·USB 스캐너도 여기로) -->
  <div class="mput">
    <div class="mput__head">
      <div class="mput__tabs">
        <button type="button" :class="['mput__tab', { 'is-on': mode === 'putaway' }]" @click="setMode('putaway')">上架<small>(재고확정)</small></button>
        <button type="button" :class="['mput__tab', { 'is-on': mode === 'pallet' }]" @click="setMode('pallet')">托盘箱号确认<small>(파렛트↔박스)</small></button>
        <!-- 扫描出库 停用(2026-10-06 YZY) — 출고완료는 출고리스트 업로드로만 -->
        <button type="button" class="mput__tab" disabled>扫描出库<small>(停用)</small></button>
      </div>
      <!-- 扫描出库: 파렛트 없이 박스만 찍으면 그 박스의 출고대기 건이 바로 출고완료(PC PalletBoxScan 의 无托盘号 扫描出库 와 같은 API) -->
      <div v-if="mode === 'scanout'" class="mput__active is-out">
        <small>扫描出库已停用(스캔 출고 중지)</small>
        <div style="font-size:16px">{{ scanOutOffMsg }}</div>
      </div>
      <div v-else :class="['mput__active', { 'is-empty': !active }]">
        <small>{{ mode === 'putaway' ? '当前库位(현재 위치)' : '当前托盘(현재 파렛트)' }}</small>
        <div>{{ active || (mode === 'putaway' ? '先扫库位号' : '先扫托盘号') }}</div>
      </div>
    </div>

    <!-- 카메라 -->
    <div class="mput__cam">
      <video ref="video" playsinline muted :class="{ 'is-on': camOn }"></video>
      <canvas ref="canvas" style="display:none"></canvas>
      <!-- 이 띠 안만 인식한다(fastScanner centerBand) — 바코드를 가로로 띠에 맞춘다 -->
      <div v-if="camOn" class="mput__aim"></div>
      <div v-if="!camOn" class="mput__cam-off">
        <button type="button" class="btn btn-primary btn-lg" @click="startCamera">📷 打开相机<small>(카메라 켜기)</small></button>
        <!-- 실시간 카메라가 막힌 기기용 — 사진 한 장 찍어 인식 -->
        <label class="btn btn-outline-light btn-sm mt-2 mb-0">🖼 拍照识别<small>(사진으로 인식)</small>
          <input type="file" accept="image/*" capture="environment" style="display:none" @change="onPhoto">
        </label>
        <div v-if="camError" class="small text-danger mt-2">{{ camError }}</div>
      </div>
      <button v-else type="button" class="btn btn-sm btn-light mput__cam-stop" @click="stopCamera">■ 关闭(끄기)</button>
      <div v-if="camOn" class="mput__engine">{{ engine }}</div>
    </div>

    <!-- 수동 입력(카메라 안 될 때 · 블루투스/USB 스캐너) -->
    <div class="mput__input">
      <input ref="manual" v-model="manual" type="text" class="form-control" inputmode="text" autocomplete="off"
             :placeholder="mode === 'scanout' ? '箱号 → 直接出库(박스번호 → 바로 출고)' : active ? (mode === 'putaway' ? '商品条码/箱号(상품·박스)' : '箱号(박스번호)') : (mode === 'putaway' ? '库位号(위치번호)' : '托盘号(파렛트번호)')"
             :disabled="busy" @keyup.enter="onManual">
      <button type="button" class="btn btn-success" :disabled="busy || !manual.trim()" @click="onManual">OK</button>
    </div>
    <div v-if="active && mode !== 'scanout'" class="mput__clear"><a href="#" @click.prevent="active = ''">{{ mode === 'putaway' ? '结束库位(위치 해제)' : '结束托盘(파렛트 해제)' }}</a></div>
    <button v-if="mode === 'scanout' && lastOut" type="button" class="btn btn-warning btn-block mt-2" :disabled="busy" @click="undoLast">
      ↩ 撤销 {{ lastOut.label }}<small>(직전 박스 출고 되돌리기)</small>
    </button>

    <!-- 같은 바코드가 여러 건이면 고르게 한다 -->
    <div v-if="candidates.length" class="mput__cands">
      <div class="small text-warning">多个匹配，请选择(여러 건 — 하나를 고르세요)</div>
      <button v-for="c in candidates" :key="c._id" type="button" class="btn btn-outline-primary btn-block btn-sm" @click="pickCandidate(c)">
        {{ c.companyid }} · {{ optionText(c) || c.상품명 }} · {{ qtyOf(c) }}
      </button>
    </div>

    <div class="mput__logs">
      <div v-for="(l, i) in logs" :key="'l' + i" :class="['mput__log', l.ok ? 'is-ok' : 'is-err']">
        <span class="mput__time">{{ l.time }}</span> {{ l.text }}
      </div>
      <div v-if="!logs.length" class="text-muted small text-center py-3">扫描结果会显示在这里(스캔 결과가 여기 쌓입니다)</div>
    </div>
  </div>
</template>

<script>
import { resolveScan, confirmAt, looksLikePallet, mapBoxToPallet, undoScanOut, optionText, qtyOf, nowText, SCAN_OUT_OFF_MSG } from './putawayScan';
import FastScanner, { SCAN_FORMATS, CAMERA_1280, centerBand } from '@/utils/fastScanner';

const MODES = ['putaway', 'pallet', 'scanout'];

const DUP_MS = 2000;     // 같은 값 2초 안 재인식은 무시
// 인식 띠 — 화면 세로 가운데 45%(아래 .mput__aim 과 같은 비율)
const BAND_RATIO = 0.45;
const MAX_LOGS = 80;

export default {
  name: 'MobilePutaway',
  data() {
    return {
      mode: MODES.includes(this.$route.query.mode) ? this.$route.query.mode : 'putaway',
      active: '',
      lastOut: null,
      scanOutOffMsg: SCAN_OUT_OFF_MSG,
      manual: '',
      busy: false,
      candidates: [],
      logs: [],
      camOn: false,
      camError: '',
      engine: '',
      stream: null,
      audio: null
    };
  },
  created() {
    // 바코드 연속 인식(공용) — 반응형일 필요 없어 data 밖에 둔다
    this.scanner = new FastScanner({ tag: '[手机扫码]', dupMs: DUP_MS });
    // 서버 확인을 순서대로 — 앞 건 응답을 기다리는 동안에도 다음 바코드를 인식해 줄 세운다
    this.chain = Promise.resolve();
  },
  beforeDestroy() {
    this.stopCamera();
  },
  methods: {
    optionText,
    qtyOf,
    setMode(m) {
      if (this.mode === m) return;
      this.mode = m;
      this.active = '';
      this.candidates = [];
      this.$router.replace({ query: Object.assign({}, this.$route.query, { mode: m }) }).catch(e => {
        if (e && e.name !== 'NavigationDuplicated') console.error('[手机扫码] 모드 주소 갱신 실패', e);
      });
    },
    log(text, ok) {
      this.logs.unshift({ time: nowText(), text, ok: !!ok });
      if (this.logs.length > MAX_LOGS) this.logs.length = MAX_LOGS;
    },
    // 인식 알림 — 진동 + 짧은 삑(실패는 낮은 음 두 번)
    feedback(ok) {
      try {
        if (navigator.vibrate) navigator.vibrate(ok ? 80 : [60, 60, 60]);
        if (!this.audio) this.audio = new (window.AudioContext || window.webkitAudioContext)();
        const play = (freq, at) => {
          const o = this.audio.createOscillator();
          const g = this.audio.createGain();
          o.frequency.value = freq;
          g.gain.value = 0.15;
          o.connect(g);
          g.connect(this.audio.destination);
          o.start(this.audio.currentTime + at);
          o.stop(this.audio.currentTime + at + 0.09);
        };
        if (ok) play(1200, 0); else { play(400, 0); play(400, 0.15); }
      } catch (e) {
        console.error('스캔 알림음 오류:', e);
      }
    },
    // ── 카메라 ──
    async startCamera() {
      this.camError = '';
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.camError = '此浏览器不支持相机(카메라를 쓸 수 없는 브라우저) — 请用下面的输入框(아래 입력칸 사용)';
        return;
      }
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ video: CAMERA_1280, audio: false });
        const v = this.$refs.video;
        v.srcObject = this.stream;
        await v.play();
        this.camOn = true;
        this.engine = await this.scanner.start({
          video: v,
          canvas: this.$refs.canvas,
          formats: () => this.scanFormats(),
          roi: centerBand(BAND_RATIO),
          onDetect: val => this.onDetected(val)
        });
      } catch (e) {
        console.error('카메라 시작 실패:', e);
        this.camError = '相机打开失败(카메라를 열지 못했습니다): ' + e.message;
        this.stopCamera();
      }
    },
    // 지금 찾을 포맷 — 위치·파렛트·박스 라벨은 CODE128, 위치를 잡은 上架 는 상품 바코드(EAN 등)도
    scanFormats() {
      return this.mode === 'putaway' && this.active ? SCAN_FORMATS.PRODUCT : SCAN_FORMATS.LABEL;
    },
    decodeSrc(src) {
      return this.scanner.decodeSrc(src, this.scanFormats());
    },
    onPhoto(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const v = await this.decodeSrc(reader.result);
          if (v) this.handle(v);
          else { this.log('照片里没识别到条码(사진에서 바코드를 못 읽음)', false); this.feedback(false); }
        } catch (err) {
          console.error('사진 바코드 인식 오류:', err);
          this.log('识别错误(인식 오류): ' + err.message, false);
        }
      };
      reader.onerror = () => console.error('사진 읽기 실패:', file.name);
      reader.readAsDataURL(file);
    },
    stopCamera() {
      this.scanner.stop();
      if (this.stream) this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
      this.camOn = false;
    },
    // 카메라 인식 — 서버 응답을 기다리지 않고 바로 진동·삑, 처리는 줄 세워 순서대로(실패면 처리 뒤 낮은 음 두 번)
    onDetected(v) {
      this.feedback(true);
      this.chain = this.chain.then(() => this.handle(v, true));
    },
    onManual() {
      const v = this.manual.trim();
      this.manual = '';
      if (v) this.handle(v);
      this.$nextTick(() => { if (this.$refs.manual) this.$refs.manual.focus(); });
    },
    // ── 처리 ──
    async handle(v, beeped) {
      this.candidates = [];
      this.busy = true;
      let ok = false;
      try {
        ok = this.mode === 'putaway' ? await this.handlePutaway(v)
          : this.mode === 'scanout' ? await this.handleScanOut(v) : await this.handlePallet(v);
      } catch (e) {
        console.error('스캔 처리 오류:', e);
        this.log('错误(오류): ' + e.message, false);
      } finally {
        this.busy = false;
        if (!beeped || !ok) this.feedback(ok);
      }
    },
    async handlePutaway(v) {
      const res = await resolveScan(v);
      if (res.type === 'location') {
        this.active = res.location;
        this.log('📍 ' + res.location + (res.saved ? '' : ' (未保存/목록에 없음)'), true);
        return true;
      }
      if (!this.active) {
        this.log('请先扫库位号(위치번호 먼저): ' + v, false);
        return false;
      }
      if (res.type === 'box') {
        if (!res.items.length) {
          this.log('📦 ' + v + ' — 箱内没有待上架商品(재고대기 없음)', false);
          return false;
        }
        return this.confirmItems(res.items, '📦 ' + v);
      }
      if (res.type === 'product') {
        if (res.items.length > 1) {
          this.candidates = res.items;
          return true;
        }
        return this.confirmItems(res.items);
      }
      this.log('未找到(재고대기 상품·박스 아님): ' + v, false);
      return false;
    },
    async confirmItems(items, label) {
      const loc = this.active;
      const { results, skipped } = await confirmAt(items, loc);
      results.forEach(r => {
        const it = items.find(x => String(x._id) === String(r._id)) || {};
        this.log('✅ ' + (it.상품명 || it.바코드 || r._id) + ' ' + optionText(it) + ' → ' + loc + (label ? ' [' + label + ']' : ''), true);
      });
      skipped.forEach(s => {
        const it = items.find(x => String(x._id) === String(s._id)) || {};
        this.log('✖ ' + (it.상품명 || s._id) + ' — ' + s.reason, false);
      });
      return results.length > 0 && !skipped.length;
    },
    async pickCandidate(c) {
      this.candidates = [];
      this.busy = true;
      let ok = false;
      try {
        ok = await this.confirmItems([c]);
      } catch (e) {
        console.error('스캔 재고확정 오류:', e);
        this.log('上架错误(재고확정 오류): ' + e.message, false);
      } finally {
        this.busy = false;
        this.feedback(ok);
      }
    },
    async handlePallet(v) {
      if (v.toUpperCase() === 'NOPALLET') {
        // NOPALLET(=扫描出库)은 停用(2026-10-06) — 출고완료는 출고리스트 업로드로만
        this.log('📤 ' + SCAN_OUT_OFF_MSG, false);
        return false;
      }
      if (looksLikePallet(v)) {
        this.active = v;
        this.log('🔲 ' + this.active, true);
        return true;
      }
      if (!this.active) {
        this.log('请先扫托盘号(파렛트번호 먼저): ' + v, false);
        return false;
      }
      await mapBoxToPallet(v, this.active);
      this.log('✅ ' + v + ' → ' + this.active, true);
      return true;
    },
    // 扫描出库 停用(2026-10-06 YZY) — 예전 ?mode=scanout 주소로 들어와도 서버를 부르지 않고 안내만 한다
    async handleScanOut(v) {
      this.log('✖ ' + v + ' — ' + SCAN_OUT_OFF_MSG, false);
      return false;
    },
    async undoLast() {
      const last = this.lastOut;
      if (!last) return;
      if (!confirm('撤销 ' + last.label + ' 的出库吗？(이 박스의 출고완료를 되돌릴까요?)')) return;
      this.busy = true;
      let ok = false;
      try {
        const res = await undoScanOut(last);
        this.lastOut = null;
        ok = !res.skipped.length;
        this.log('↩ ' + last.label + ' 撤销(되돌림) ' + res.reverted.length + '件' + (res.skipped.length ? ' / 未撤销(못 되돌림) ' + res.skipped.length + ': ' + res.skipped[0].reason : ''), ok);
      } catch (e) {
        console.error('스캔 출고 되돌리기 오류:', e);
        this.log('✖ 撤销失败(되돌리기 실패) — ' + e.message, false);
      } finally {
        this.busy = false;
        this.feedback(ok);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.mput {
  max-width: 520px;
  margin: 0 auto;
  padding: 8px;
  font-size: 15px;
  &__tabs { display: flex; gap: 6px; margin-bottom: 8px; }
  &__tab {
    flex: 1;
    padding: 10px 4px;
    border: 1px solid #3b6fd8;
    border-radius: 6px;
    background: #fff;
    color: #3b6fd8;
    font-weight: 600;
    &.is-on { background: #3b6fd8; color: #fff; }
    &.is-out { border-color: #dc3545; color: #dc3545; &.is-on { background: #dc3545; color: #fff; } }
  }
  &__active {
    text-align: center;
    padding: 6px;
    border: 2px solid #28a745;
    border-radius: 8px;
    background: #eafaf0;
    small { color: #555; }
    div { font-size: 30px; font-weight: 800; color: #1e7e34; line-height: 1.2; word-break: break-all; }
    &.is-empty { border-color: #ccc; background: #f6f6f6; div { color: #999; font-size: 20px; } }
    &.is-out { border-color: #dc3545; background: #fdecee; div { color: #b02a37; font-size: 24px; } }
  }
  &__cam {
    position: relative;
    margin: 8px 0;
    min-height: 140px;
    background: #111;
    border-radius: 8px;
    overflow: hidden;
    video { width: 100%; display: none; &.is-on { display: block; } }
  }
  &__cam-off {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-align: center;
    padding: 8px;
  }
  &__cam-stop { position: absolute; right: 6px; top: 6px; }
  &__engine { position: absolute; left: 6px; bottom: 4px; font-size: 11px; color: #ddd; }
  &__aim {
    position: absolute;
    left: 4%;
    right: 4%;
    top: 27.5%;
    height: 45%;
    border: 2px solid rgba(40, 167, 69, 0.9);
    border-radius: 6px;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25);
    pointer-events: none;
  }
  &__input { display: flex; gap: 6px; input { font-size: 17px; } }
  &__clear { text-align: right; font-size: 13px; margin-top: 4px; }
  &__cands { margin-top: 6px; }
  &__logs { margin-top: 8px; }
  &__log {
    padding: 6px 8px;
    border-bottom: 1px solid #eee;
    font-size: 14px;
    &.is-ok { color: #1e7e34; }
    &.is-err { color: #c82333; background: #fff5f5; }
  }
  &__time { color: #999; font-size: 12px; }
}
</style>
